import { PitchDetector } from './core/PitchDetector';
import { AudioInputManager } from './core/AudioInputManager';
import { INSTRUMENTS, getInstrument } from './config/instruments';
import {
  TunerConfig,
  PitchInfo,
  TunerEventType,
  TunerEventHandler,
  ErrorEvent,
} from './types';
import {
  frequencyToNoteName,
  findClosestFrequency,
  isInTune,
} from './utils/noteUtils';

/**
 * Main GuitarTuner class - public API
 */
export class GuitarTuner {
  private pitchDetector: PitchDetector | null = null;
  private audioManager: AudioInputManager;
  private eventHandlers: Map<TunerEventType, Set<TunerEventHandler>> = new Map();

  // Configuration
  private config: Required<TunerConfig>;
  private targetFrequencies: number[] = [];

  constructor() {
    this.audioManager = new AudioInputManager();

    // Default configuration
    this.config = {
      instrument: 'guitar',
      tuning: 'standard',
      referenceFrequency: 440,
      tolerance: 5,
      bufferSize: 4096,
      minCorrelation: 0.9,
    };
  }

  /**
   * Initialize the tuner with configuration
   */
  async init(config: TunerConfig = {}): Promise<void> {
    // Merge with defaults
    this.config = { ...this.config, ...config };

    // Set up tuning
    this.updateTuning();
  }

  /**
   * Start the tuner (request microphone access and begin detection)
   */
  async start(): Promise<void> {
    try {
      // Initialize audio manager
      await this.audioManager.start((buffer) => this.onAudioData(buffer));

      // Create pitch detector with sample rate
      const sampleRate = this.audioManager.getSampleRate();
      this.pitchDetector = new PitchDetector(
        sampleRate,
        this.config.minCorrelation
      );
    } catch (error) {
      this.emitError(
        'Failed to start tuner',
        error instanceof Error ? error : undefined
      );
      throw error;
    }
  }

  /**
   * Stop the tuner
   */
  stop(): void {
    this.audioManager.stop();
    this.pitchDetector = null;
  }

  /**
   * Set the instrument
   */
  setInstrument(instrument: string): void {
    this.config.instrument = instrument;
    this.updateTuning();
  }

  /**
   * Set the tuning (preset name or custom frequencies)
   */
  setTuning(tuning: string | number[]): void {
    this.config.tuning = tuning;
    this.updateTuning();
  }

  /**
   * Set reference frequency for A4
   */
  setReferenceFrequency(frequency: number): void {
    this.config.referenceFrequency = frequency;
    this.updateTuning();
  }

  /**
   * Set tolerance in cents
   */
  setTolerance(cents: number): void {
    this.config.tolerance = cents;
  }

  /**
   * Set minimum correlation threshold
   */
  setMinCorrelation(correlation: number): void {
    this.config.minCorrelation = correlation;
    if (this.pitchDetector) {
      this.pitchDetector.setMinCorrelation(correlation);
    }
  }

  /**
   * Get current pitch (last detected)
   */
  getCurrentPitch(): PitchInfo | null {
    // This would store the last detected pitch
    // For now, returns null as we process in real-time
    return null;
  }

  /**
   * Register event handler
   */
  on(event: TunerEventType, handler: TunerEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  /**
   * Unregister event handler
   */
  off(event: TunerEventType, handler: TunerEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stop();
    this.eventHandlers.clear();
  }

  /**
   * Update tuning based on current configuration
   */
  private updateTuning(): void {
    if (Array.isArray(this.config.tuning)) {
      // Custom tuning
      this.targetFrequencies = this.config.tuning;
    } else {
      // Preset tuning
      const instrument = getInstrument(this.config.instrument);
      if (!instrument) {
        this.emitError(`Unknown instrument: ${this.config.instrument}`);
        return;
      }

      const tuning = instrument.tunings[this.config.tuning];
      if (!tuning) {
        this.emitError(
          `Unknown tuning: ${this.config.tuning} for instrument ${this.config.instrument}`
        );
        return;
      }

      this.targetFrequencies = tuning.frequencies;
    }
  }

  /**
   * Handle audio data from microphone
   */
  private onAudioData(buffer: Float32Array): void {
    if (!this.pitchDetector) {
      return;
    }

    const result = this.pitchDetector.detectPitch(buffer);
    if (!result) {
      return; // No pitch detected
    }

    const { frequency, correlation } = result;

    // Find closest target frequency
    const closest = findClosestFrequency(frequency, this.targetFrequencies);
    const note = frequencyToNoteName(frequency, this.config.referenceFrequency);
    const cents = closest.cents;
    const inTuneFlag = isInTune(
      frequency,
      closest.frequency,
      this.config.tolerance
    );

    const pitchInfo: PitchInfo = {
      frequency,
      note,
      cents,
      correlation,
      stringNumber: closest.index + 1, // 1-based
      targetFrequency: closest.frequency,
      inTune: inTuneFlag,
    };

    // Emit events
    this.emit('pitchDetected', pitchInfo);

    if (Math.abs(cents) < 50) {
      // Close enough to consider it a match
      this.emit('noteMatched', pitchInfo);
    }

    if (inTuneFlag) {
      this.emit('inTune', pitchInfo);
    }
  }

  /**
   * Emit an event
   */
  private emit(event: TunerEventType, data: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  /**
   * Emit an error event
   */
  private emitError(message: string, error?: Error): void {
    const errorEvent: ErrorEvent = { message, error };
    this.emit('error', errorEvent);
  }

  /**
   * Get available instruments
   */
  static getInstruments(): string[] {
    return Object.keys(INSTRUMENTS);
  }

  /**
   * Get available tunings for an instrument
   */
  static getTunings(instrument: string): string[] {
    const inst = getInstrument(instrument);
    return inst ? Object.keys(inst.tunings) : [];
  }
}
